/**
 * Run state and run controls (prompt.md §20, §21, §33, §48, §65).
 *
 * WHY these live together: a run's badge, its controls and its pause explanation
 * are one story. Showing "Paused" without saying *which* limit was reached, or
 * offering a steer box on a completed run, is exactly the confusion this
 * component set exists to prevent. All three read from the same pure
 * descriptors in `logic/runState.ts`, so the labels cannot drift apart.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { Run, RunPauseReason, RunState } from "@dayvise/domain";

import { Badge, EmptyState, Spinner } from "../primitives/Feedback.tsx";
import { Button, IconButton } from "../primitives/Button.tsx";
import { TextArea } from "../primitives/Fields.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { formatDuration, formatRelativeTime } from "../logic/format.ts";
import { runControlAvailability, describeRunState, toneColor, toneSurface, type StatusTone } from "../logic/runState.ts";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";

export interface RunStateBadgeProps {
  readonly state: RunState;
  readonly pauseReason?: RunPauseReason | null;
  readonly showDetail?: boolean;
}

/**
 * A run's state as a badge. The spinner is what tells a user the difference
 * between "Running" and "Completed" at a glance, and the detail line is what
 * tells them a paused run still holds their work.
 */
export function RunStateBadge({ state, pauseReason = null, showDetail = false }: RunStateBadgeProps) {
  const descriptor = describeRunState(state, pauseReason);
  return (
    <span style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
      <Badge tone={descriptor.tone}>{descriptor.label}</Badge>
      {descriptor.spinning ? <Spinner size="sm" label={null} /> : null}
      {showDetail ? <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{descriptor.detail}</span> : null}
    </span>
  );
}

export interface RunControlsProps {
  readonly state: RunState;
  readonly pauseReason?: RunPauseReason | null;
  readonly onSteer?: (message: string) => void | Promise<void>;
  readonly onPause?: () => void | Promise<void>;
  readonly onResume?: () => void | Promise<void>;
  readonly onCancel?: () => void | Promise<void>;
  readonly busy?: boolean;
  readonly steerDraft?: string;
  readonly onSteerDraftChange?: (value: string) => void;
}

/**
 * Run controls, derived from the state machine so the UI never offers an action
 * the API would reject. Steering is a text box rather than a "send" button
 * because a steer is not a new message — it changes what the *current* run is
 * doing (prompt.md §20).
 */
export function RunControls({ state, pauseReason = null, onSteer, onPause, onResume, onCancel, busy = false, steerDraft = "", onSteerDraftChange }: RunControlsProps) {
  const [pending, setPending] = useState(false);
  const [pauseRequested, setPauseRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setPauseRequested(false); setError(null); }, [state]);
  const perform = async (action: () => void | Promise<void>, paused = false) => {
    setPending(true);
    setError(null);
    try { await action(); if (paused) setPauseRequested(true); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The run control could not be completed."); }
    finally { setPending(false); }
  };
  const controlsBusy = busy || pending;
  const availability = runControlAvailability(state);
  const descriptor = describeRunState(state, pauseReason);
  const nothingAvailable = !availability.canSteer && !availability.canPause && !availability.canResume && !availability.canCancel;

  if (nothingAvailable) {
    return <span style={MUTED_TEXT}>{descriptor.detail}</span>;
  }

  return (
    <div style={stackStyle("var(--dayvise-space-sm)")}>
      {availability.canSteer && onSteer ? (
        <div style={stackStyle("var(--dayvise-space-xs)")}>
          <TextArea
            label="Steer this run"
            hint="Changes what the current run does next. It does not start a new run and does not edit history."
            rows={2}
            value={steerDraft}
            disabled={controlsBusy}
            onChange={(event) => onSteerDraftChange?.(event.target.value)}
          />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              size="sm"
              loading={controlsBusy}
              disabled={steerDraft.trim().length === 0}
              onClick={() => {
                void perform(() => onSteer(steerDraft.trim()));
              }}
            >
              Steer
            </Button>
          </div>
        </div>
      ) : null}

      <div style={rowStyle("var(--dayvise-space-xs)")}>
        {availability.canResume && onResume ? (
          <Button variant="primary" size="sm" loading={controlsBusy} onClick={() => void perform(onResume)}>
            Resume from checkpoint
          </Button>
        ) : null}
        {availability.canPause && onPause ? (
          <Button variant="secondary" size="sm" loading={controlsBusy} disabled={pauseRequested} onClick={() => void perform(onPause, true)}>
            {pauseRequested ? "Pause requested" : "Pause"}
          </Button>
        ) : null}
        {availability.canCancel && onCancel ? (
          <Button variant="danger" size="sm" loading={controlsBusy} onClick={() => void perform(onCancel)}>
            Cancel run
          </Button>
        ) : null}
      </div>
      {pauseRequested && availability.canPause ? <span role="status" style={MUTED_TEXT}>Waiting for the next safe boundary and saved checkpoint…</span> : null}
      {error ? <span role="alert">{error}</span> : null}
    </div>
  );
}

export interface RunSummaryProps {
  readonly run: Run;
  readonly conversationHref?: string;
  readonly onOpenConversation?: () => void;
}

/** A compact row describing one run, for the runs list. */
export function RunSummary({ run, onOpenConversation }: RunSummaryProps) {
  const descriptor = describeRunState(run.state, run.pauseReason);
  const elapsed = run.startedAt ? formatDuration(Date.parse(run.finishedAt ?? new Date().toISOString()) - Date.parse(run.startedAt)) : null;
  return (
    <Card
      title={descriptor.label}
      subtitle={`Started ${formatRelativeTime(run.createdAt)}${elapsed === null ? "" : ` · ${elapsed}`}`}
      footer={onOpenConversation ? <Button size="sm" onClick={onOpenConversation}>Open conversation</Button> : undefined}
    >
      <div style={stackStyle("var(--dayvise-space-xxs)")}>
        <RunStateBadge state={run.state} pauseReason={run.pauseReason} showDetail />
        <span style={MUTED_TEXT}>
          {run.trigger.replace(/_/g, " ")} · {run.usage.toolCalls} tool calls · {run.usage.inputTokens + run.usage.outputTokens} tokens
        </span>
        {run.error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{run.error}</span> : null}
      </div>
    </Card>
  );
}

export interface RunListProps {
  readonly runs: readonly Run[];
  readonly onOpenConversation?: (run: Run) => void;
  readonly emptyLabel?: string;
}

export function RunList({ runs, onOpenConversation, emptyLabel = "No runs yet" }: RunListProps) {
  if (runs.length === 0) {
    return <EmptyState title={emptyLabel} description="Runs appear here while they are active and after they finish." />;
  }
  return (
    <div style={stackStyle("var(--dayvise-space-sm)")}>
      {runs.map((run) => (
        <RunSummary key={run.runId} run={run} onOpenConversation={onOpenConversation ? () => onOpenConversation(run) : undefined} />
      ))}
    </div>
  );
}

/** Live-connection state for the run stream (prompt.md §49, §76). */
export type StreamStatus = "connecting" | "live" | "reconnecting" | "closed";

const STREAM_LABELS: Readonly<Record<StreamStatus, string>> = {
  connecting: "Connecting…",
  live: "Live",
  reconnecting: "Reconnecting…",
  closed: "Disconnected",
};

export function StreamStatusIndicator({ status, lastSequence }: { readonly status: StreamStatus; readonly lastSequence?: number }) {
  const tone: StatusTone = status === "live" ? "success" : status === "closed" ? "danger" : "warning";
  return (
    <span style={rowStyle("var(--dayvise-space-xs)", "flex-start")} title={lastSequence === undefined ? undefined : `Last event #${lastSequence}`}>
      <span
        aria-hidden="true"
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "var(--dayvise-radius-pill)",
          background: toneColor(tone),
          display: "inline-block",
        }}
      />
      <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{STREAM_LABELS[status]}</span>
    </span>
  );
}

/** A labelled key/value block used by the run detail screen. */
export function RunFacts({ entries }: { readonly entries: readonly { readonly label: string; readonly value: ReactNode }[] }) {
  return (
    <dl style={{ ...stackStyle("var(--dayvise-space-xs)"), margin: 0 }}>
      {entries.map((entry) => (
        <div key={entry.label} style={rowStyle("var(--dayvise-space-sm)", "space-between")}>
          <dt style={{ ...MUTED_TEXT, margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{entry.label}</dt>
          <dd style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{entry.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export { toneSurface };
export { IconButton };
